import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Debounce timer for scroll handler to prevent rapid state flapping
   * @type {number | null}
   */
  #scrollDebounce = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Directional collapse/expand tuning for sticky="always"
   * Accumulates deltas to avoid jitter while the user micro-scrolls
   */
  #downAccumulated = 0;
  #upAccumulated = 0;
  #collapseAfter = 50; // px scrolled down before collapsing
  #expandAfter = 50; // px scrolled up before expanding
  #minEnableThreshold = 12; // ignore tiny scrolls from top

  /**
   * Cached actions element (account/cart)
   * @type {HTMLElement | null}
   */
  #actionsEl = null;

  /**
   * Cached search action element
   * @type {HTMLElement | null}
   */
  #searchEl = null;

  /**
   * Tracks whether the top row is considered collapsed (hidden) while in
   * sticky="always" mode. Using this state allows us to introduce
   * hysteresis (separate expand/collapse thresholds) to prevent bouncing
   * when the header height changes the page scroll position.
   * @type {boolean}
   */
  #collapsedTopRow = false;

  /** Close any open mega menus before collapsing header */
  #closeOpenMenus = () => {
    const menuRoot = this.querySelector('header-menu');
    if (!menuRoot) return;
    /** @type {NodeListOf<HTMLElement>} */
    const openTriggers = menuRoot.querySelectorAll('.menu-list__link[aria-expanded="true"]');
    openTriggers.forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * Move actions between top and bottom right columns on desktop,
   * depending on whether we are at the very top or scrolled.
   */
  #syncStickyActions = () => {
    // Temporarily disabled to avoid reflow jitter while we use full-header sticky
    return;
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!isDesktop) return;

    if (!this.#actionsEl) this.#actionsEl = this.querySelector('header-actions');
    if (!this.#searchEl) this.#searchEl = this.querySelector('.header__row--top .search-action');

    const actions = this.#actionsEl;
    const search = this.#searchEl;

    if (!actions && !search) return;

    const topRight = this.querySelector('.header__row--top .header__column--right');

    // Ensure there is a right column in the bottom row; create one if missing
    let bottomRight = this.querySelector('.header__row--bottom .header__column--right');
    if (!bottomRight) {
      const bottomColumns = this.querySelector('.header__row--bottom .header__columns');
      if (bottomColumns) {
        bottomRight = document.createElement('div');
        bottomRight.className = 'header__column header__column--right';
        // If there is no left column and we have a sticky logo, insert a left column with it
        const stickyLogoUrl = this.getAttribute('data-sticky-logo');
        const stickyLogoWidth = this.getAttribute('data-sticky-logo-width') || '28';
        const hasLeft = this.querySelector('.header__row--bottom .header__column--left');
        if (stickyLogoUrl && !hasLeft) {
          const left = document.createElement('div');
          left.className = 'header__column header__column--left header__sticky-logo';
          const link = document.createElement('a');
          link.className = 'header__sticky-logo-link';
          link.setAttribute('href', '/');
          link.setAttribute('aria-label', document.title || 'Home');
          const img = document.createElement('img');
          img.src = stickyLogoUrl;
          img.width = Math.max(4, parseInt(stickyLogoWidth, 10));
          img.style.height = 'auto';
          link.appendChild(img);
          left.appendChild(link);
          bottomColumns.prepend(left);
        }
        bottomColumns.appendChild(bottomRight);
      }
    }
    if (!topRight || !bottomRight) return;

    const atTop = this.dataset.stickyState !== 'active';
    const actionsInTop = actions ? !!actions.closest('.header__row--top') : false;
    const searchInTop = search ? !!search.closest('.header__row--top') : false;

    if (atTop) {
      if (actions && !actionsInTop) topRight.appendChild(actions);
      if (search && !searchInTop) topRight.appendChild(search);
    } else {
      if (actions && actionsInTop) bottomRight.appendChild(actions);
      if (search && searchInTop) bottomRight.appendChild(search);
    }
  };

  /**
   * Update the top row height CSS variable for smooth animations
   */
  #updateTopRowHeight = () => {
    const topRow = this.refs.headerRowTop;
    if (topRow) {
      const topRowHeight = topRow.getBoundingClientRect().height;
      this.style.setProperty('--header-row-top-height', `${topRowHeight}px`);
    }
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Update top row height for sticky collapse calculations
    this.#updateTopRowHeight();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        // In sticky="always" mode, avoid overriding sticky state based on
        // intersection because the header height changes during transitions
        // can cause rapid toggling near the threshold. The scroll handler
        // owns stickyState in this mode. We still update the theme color.
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    // Debounce to avoid rapid toggling near thresholds
    if (this.#scrollDebounce) {
      cancelAnimationFrame(this.#scrollDebounce);
      this.#scrollDebounce = null;
    }

    this.#scrollDebounce = requestAnimationFrame(() => {
    // If any blocking UI like the cart drawer is open, freeze header state
    const drawerOpen = document.querySelector('cart-drawer-component[open]');
    if (drawerOpen) return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      // Don't activate sticky header until user scrolls down a bit
      const stickyActivationThreshold = 50;
      if (scrollTop <= stickyActivationThreshold) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        this.#downAccumulated = 0;
        this.#upAccumulated = 0;
      } else {
        // Once we scroll past threshold, header becomes sticky
        const wasInactive = this.dataset.stickyState !== 'active';
        this.dataset.stickyState = 'active';
        
        const delta = scrollTop - this.#lastScrollTop;
        if (delta > 0) {
          // Scrolling down → accumulate and consider collapsing
          this.#downAccumulated += delta;
          this.#upAccumulated = 0;
          
          // Collapse after accumulating enough scroll distance
          if (scrollTop > this.#minEnableThreshold && this.#downAccumulated >= this.#collapseAfter) {
            this.dataset.scrollDirection = 'down';
            this.#downAccumulated = 0;
          } else if (wasInactive) {
            // Just became sticky, show both rows initially (direction = 'up')
            this.dataset.scrollDirection = 'up';
          }
        } else if (delta < 0) {
          // Scrolling up → only expand top row when near the top of the page
          this.#upAccumulated += -delta;
          this.#downAccumulated = 0;
          
          // Define "near top" threshold (e.g., within 200px of top)
          const nearTopExpandThreshold = 200;
          
          // Only expand if we're near the top of the page
          if (scrollTop <= nearTopExpandThreshold) {
            this.dataset.scrollDirection = 'up';
            this.#upAccumulated = 0;
          } else {
            // Stay collapsed when scrolling up but far from top
            this.dataset.scrollDirection = 'down';
          }
        }
      }

      this.#lastScrollTop = scrollTop;
      this.#syncStickyActions();
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      // Add hysteresis near the top to avoid bounce when micro-scrolling
      const topThreshold = 30;
      if (scrollTop <= topThreshold) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
    this.#syncStickyActions();
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      // Calculate top row height immediately on load
      this.#updateTopRowHeight();
      
      // Only observe intersection when not always sticky. In always mode
      // the scroll handler manages the state to avoid boundary flicker.
      this.#observeStickyPosition(stickyMode !== 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
        if (stickyMode === 'always') this.#handleWindowScroll();
        this.#syncStickyActions();
        window.addEventListener('resize', this.#syncStickyActions);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
